import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunks
export const checkGmailAuth = createAsyncThunk(
  'gmail/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/gmail/auth/status');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check Gmail auth');
    }
  }
);

export const getGmailAuthUrl = createAsyncThunk(
  'gmail/getAuthUrl',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/gmail/auth/url');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get auth URL');
    }
  }
);

export const authorizeGmail = createAsyncThunk(
  'gmail/authorize',
  async (code, { rejectWithValue }) => {
    try {
      const response = await api.post('/gmail/auth/authorize', { code });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to authorize Gmail');
    }
  }
);

export const fetchInbox = createAsyncThunk(
  'gmail/fetchInbox',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/gmail/inbox', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch inbox');
    }
  }
);

export const fetchEmail = createAsyncThunk(
  'gmail/fetchEmail',
  async (messageId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/gmail/email/${messageId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch email');
    }
  }
);

export const markEmailAsRead = createAsyncThunk(
  'gmail/markAsRead',
  async (messageId, { rejectWithValue }) => {
    try {
      const response = await api.put(`/gmail/email/${messageId}/read`);
      return { messageId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark as read');
    }
  }
);

const gmailSlice = createSlice({
  name: 'gmail',
  initialState: {
    isAuthorized: false,
    authUrl: null,
    emails: [],
    currentEmail: null,
    loading: false,
    error: null,
    nextPageToken: null,
    total: 0
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentEmail: (state) => {
      state.currentEmail = null;
    },
    setEmailAsRead: (state, action) => {
      const email = state.emails.find(e => e.id === action.payload);
      if (email) {
        email.isRead = true;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Check Gmail Auth
      .addCase(checkGmailAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkGmailAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthorized = action.payload.isAuthorized;
      })
      .addCase(checkGmailAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthorized = false;
      })
      
      // Get Auth URL
      .addCase(getGmailAuthUrl.fulfilled, (state, action) => {
        state.authUrl = action.payload.authUrl;
      })
      
      // Authorize Gmail
      .addCase(authorizeGmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authorizeGmail.fulfilled, (state) => {
        state.loading = false;
        state.isAuthorized = true;
        state.authUrl = null;
      })
      .addCase(authorizeGmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Inbox
      .addCase(fetchInbox.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInbox.fulfilled, (state, action) => {
        state.loading = false;
        state.emails = action.payload.emails;
        state.nextPageToken = action.payload.nextPageToken;
        state.total = action.payload.total;
      })
      .addCase(fetchInbox.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Email
      .addCase(fetchEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEmail = action.payload;
      })
      .addCase(fetchEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Mark as Read
      .addCase(markEmailAsRead.fulfilled, (state, action) => {
        const email = state.emails.find(e => e.id === action.payload.messageId);
        if (email) {
          email.isRead = true;
        }
      });
  }
});

export const { clearError, clearCurrentEmail, setEmailAsRead } = gmailSlice.actions;
export default gmailSlice.reducer;